import { ref, computed, reactive, toRef } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import utils from '@/utils/utils'
import dealStatus from '@/utils/dealStatus'

import { useDdDeal } from '@/stores/due_diligence/ddDeal.store'
import { useDdClient } from '@/stores/due_diligence/ddClient.store'
import { useDdEstate } from '@/stores/due_diligence/ddEstate.store'
import { useDdVote } from '@/stores/due_diligence/ddVote.store'
import { addEntry, removeEntry, entriesHash } from '@/utils/dd/entryHelpers'
import { buildSnapshotData } from '@/utils/dd/snapshotBuilder'

export const useDueDiligence = defineStore(
  'dueDiligence',
  () => {
    const ddDeal = useDdDeal()
    const ddEstate = useDdEstate()
    const ddClient = useDdClient()
    const ddVote = useDdVote()
    const { realEstate } = storeToRefs(ddEstate)
    const clientDeals = ref([])
    const competitorDeals = ref([])
    const previouslyFundedDeals = ref([])
    const metaData = ref({})
    const templates = ref({})

    function loadData(responseData) {
      ddDeal.loadData(responseData.ddDeal)
      ddEstate.loadData(responseData)
      ddClient.loadData(responseData.clientMember)
      ddVote.loadData({ ddVotes: responseData.ddVotes || [], dealId: responseData.ddDeal?.dealId })
      clientDeals.value = responseData.clientDeals
      competitorDeals.value = responseData.competitorDeals
      previouslyFundedDeals.value = responseData.previouslyFundedDeals
      metaData.value = responseData.metaData
      templates.value = responseData.templates
      // console.log('estateDD.loadData', costsAndClaimsDefault, model.costsAndClaimsDefault)
      return Promise.resolve(this)
    }

    const viewMap = {
      totalAssets: 0,
      costsAndClaims: 1,
      estateDistribution: 2,
      heirDistribution: 3,
      atvAssignments: 4,
      followUpNotes: 5,
      // these are nested inside of total assets accordion
      realEstate: 100,
      liquidAssets: 101,
      otherAssets: 102
    }

    const viewState = reactive({
      fundingActivityClient: true,
      fundingActivityEstate: true,
      expandAllComments: false,
      expandAssetSections: false,
      expandDDSections: true,
      dd: {
        topSummary: true,
        prExecutor: true,
        fundingActivity: true,
        heirship: true,
        knownIssues: true,
        attorneyPrComms: true,
        estateAssets: true,
        clientOffsets: true,
        assetsSummary: true,
        riskReports: true,
        bottomSummary: true,
        fundingConditions: true,
        votingStatus: true
      },
      // totalAssets: true,
      // realEstate: false,
      // liquidAssets: false,
      // otherAssets: false,
      // costsAndClaims: false,
      // estateDistribution: false,
      // heirDistribution: false,
      // atvAssignments: false,
      // followUpNotes: false,
      accordionIndexes: {
        topLevelTabs: [],
        totalAssets: []
      },
      visibleSections: computed(() => {
        const pairs = utils.toPairs(viewMap)
        const topLevelIdx = viewState.accordionIndexes.topLevelTabs
        const nestedIdx = viewState.accordionIndexes.totalAssets.map((i) => i + 100)
        const allIdx = [...topLevelIdx, ...nestedIdx]
        // console.log('visibleSections', allIdx)
        return utils.filter(pairs, (pair) => allIdx.includes(pair[1])).map((pair) => pair[0])
      }),
      hiddenSections: computed(() => {
        const allKeys = utils.toPairs(viewMap).map((pair) => pair[0])
        return utils.filter(allKeys, (key) => !viewState.visibleSections.includes(key))
      }),
      toggleAASections: () => {
        if (viewState.expandAssetSections) {
          viewState.accordionIndexes.topLevelTabs = []
          viewState.accordionIndexes.totalAssets = []
        } else {
          const pairs = utils.toPairs(viewMap)
          const ids = pairs.map((arr) => arr[1])
          // console.log('toggleAASections', ids, pairs, viewMap)
          viewState.accordionIndexes.topLevelTabs = ids.filter((id) => id < 100)
          viewState.accordionIndexes.totalAssets = ids
            .filter((id) => id >= 100)
            .map((id) => id - 100)
        }
        viewState.expandAssetSections = !viewState.expandAssetSections
      },
      toggleDDSections: () => {
        const bool = !viewState.expandDDSections
        Object.keys(viewState.dd).forEach((key) => (viewState.dd[key] = bool))
        viewState.expandDDSections = !viewState.expandDDSections
      }
    })

    const currentDealStatus = ref(null)
    const isLocked = computed(() => dealStatus.ddIsLocked(currentDealStatus.value))

    // Frozen = a snapshot was stamped when the deal entered the Underwriting Queue.
    const isFrozen = computed(() => Boolean(ddDeal.model?.frozenAt))
    const hasSnapshot = computed(() => Boolean(ddDeal.model?.snapshot))

    // Assemble a point-in-time snapshot (inputs + all computed values) from current
    // store state, rounded per the precision policy. Sent on save and on send_to_uwq.
    function buildSnapshot() {
      return buildSnapshotData({
        ddDeal,
        ddEstate,
        ddClient,
        totals: totals.value,
        related: {
          clientDeals: clientDeals.value,
          competitorDeals: competitorDeals.value,
          previouslyFundedDeals: previouslyFundedDeals.value,
          metaData: metaData.value
        }
      })
    }

    // Helper Functions
    const isPersisted = computed(() => Boolean(ddDeal.model?.id))

    // Bumped (to Date.now()) after getDueDiligence() + initializeDefaults() complete.
    // DdWorksheet watches this to know when to reset the change-tracking snapshot.
    const dataLoadedAt = ref(null)

    const totals = ref({
      realEstate: computed(() => ddEstate.grandTotals.realEstate),
      liquidAssets: computed(() => ddEstate.grandTotals.liquidAssets),
      otherAssets: computed(() => ddEstate.grandTotals.otherAssets),
      netTotalAssets: computed(() => ddEstate.grandTotals.netTotalAssets),
      grossTotalAssets: computed(() => ddEstate.grandTotals.grossTotalAssets),
      costsAndClaims: computed(() => ddEstate.grandTotals.costsAndClaims),
      excludedRealEstate: computed(() => ddEstate.grandTotals.excludedRealEstate),
      excludedLiquidAssets: computed(() => ddEstate.grandTotals.excludedLiquidAssets),
      excludedOtherAssets: computed(() => ddEstate.grandTotals.excludedOtherAssets),
      residualForDist: computed(() => ddEstate.estateDistributionCalcs.residualForDist),
      remainingShareToDist: computed(() => ddClient.grandTotals.remainingShareToDist),
      atvTotalAssignments: computed(() => ddDeal.grandTotals.atvTotalAssignments),
      atvRate: computed(() => ddDeal.grandTotals.atvRate),
      atvAllowableDrop: computed(() => ddDeal.grandTotals.atvAllowableDrop),
      atvAllowableDropRate: computed(() => ddDeal.grandTotals.atvAllowableDropRate),
      atvAllowableDropDiff: computed(() => ddDeal.grandTotals.atvAllowableDropDiff),
      securedDebt: computed(() => ddEstate.grandTotals.securedDebt),
      unsecuredDebt: computed(() => ddEstate.grandTotals.unsecuredDebt),
      heirShare: computed(() => ddClient.grandTotals.heirShare),
      heirNetInheritance: computed(() => ddClient.grandTotals.heirNetInheritance)
    })

    return {
      loadData,
      ddDeal,
      ddEstate,
      ddClient,
      ddVote,
      realEstate, //: toRef(ddEstate.realEstate),
      // Helper Methods
      viewState,
      isPersisted,
      dataLoadedAt,
      currentDealStatus,
      isLocked,
      isFrozen,
      hasSnapshot,
      buildSnapshot,
      entriesHash,
      addEntry,
      removeEntry,
      // computed Totals and Calculations
      totals,
      clientDeals,
      competitorDeals,
      previouslyFundedDeals,
      metaData,
      templates
    }
  },
  {
    // Do not persist in localStorage.
    persist: false
  }
)
